import React from 'react';
import {
  TextField,
  Button
} from '@shopify/polaris';

/**
 * A reusable color picker component that includes a text field and color picker popover
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the text field
 * @param {string} props.value - Current color value (hex format)
 * @param {function} props.onChange - Function to call when color changes
 * @param {string} props.helpText - Optional help text to display under the field
 
 */
export default function ColorPickerField({
  label,
  value, // No default value here - use the one provided by the parent component
  onChange,
  helpText
}) {
  
  // Ensure valid hex color (with # prefix)
  const ensureValidHexColor = (color) => {
    // Only use fallback if color is null, undefined, or empty string
    if (color === null || color === undefined || color === '') {
      return '#000000'; // Default fallback only when truly missing
    }
    
    // If color doesn't start with #, add it
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    
    // Validate hex format - only fallback if truly invalid
    const hexRegex = /^#([A-Fa-f0-9]{3}){1,2}$/;
    if (!hexRegex.test(color)) {
      // Try to fix common issues (like extra characters)
      const fixedColor = '#' + color.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6);
      if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(fixedColor)) {
        return fixedColor;
      }
      return '#000000'; // Last resort fallback
    }
    
    return color;
  };

  // Get the appropriate default color based on the field label
  const getDefaultColorForField = (fieldLabel) => {
    // Map field labels to their default colors
    const defaultColorMap = {
      // Global styles
      'Chart Title Color': '#212b36',
      
      // Fit Guide Visualizer
      'Shape Color': '#000000',
      'Track Color': '#e6e6e6',
      'Label Color': '#637381',
      
      // Size Chart Table styles
      'Border Color': '#dfe3e8',
      'Header Background Color': '#f4f6f8',
      'Header Text Color': '#212b36',
      'Cell Text Color': '#212b36',
      'Alternate Row Text Color': '#212b36',
      'Row Alternate Color': '#f9fafb',
      
      // How to Measure section
      'Background Color': '#f4f6f8',
      'Heading Color': '#212b36',
      'Number Background Color': '#0068d9',
      'Number Text Color': '#ffffff',
      
      // Fit Guide specific styles
      'Fit Guide Background Color': '#f4f6f8',
      'Fit Guide Heading Color': '#212b36',
      'Fit Guide Text Color': '#637381',
      'Fit Guide Accent Color': '#0068d9',
      'Size Card Background Color': '#ffffff',
      'Fit Tag Background': '#f2f7fe',
      'Fit Tag Text': '#0068d9',
      
      // Footer and info styles
      'Footer Text Color': '#637381',
      'Info Text Color': '#637381'
    };
    
    return defaultColorMap[fieldLabel] || '#000000';
  };
  
  // Use the appropriate default color if value is undefined
  const effectiveValue = (value === undefined) ? getDefaultColorForField(label) : value;
  
  // Ensure the displayed value is always valid
  const displayValue = ensureValidHexColor(effectiveValue);

  const handleColorChange = (newColor) => {
    onChange(ensureValidHexColor(newColor));
  };



  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ flex: 1 }}>
          <TextField
            label={label}
            value={displayValue}
            onChange={handleColorChange}
            placeholder="#000000"
            helpText={helpText}
          />
        </div>
        <div style={{ marginTop: '20px' }}>
          <div style={{ position: 'relative' }}>
            <Button onClick={() => document.getElementById(`color-picker-${label}`).click()}>
              <div 
                style={{ 
                  width: '24px', 
                  height: '24px', 
                  background: displayValue, 
                  border: '1px solid #ddd', 
                  borderRadius: '2px' 
                }}
              ></div>
            </Button>
            <input
              id={`color-picker-${label}`}
              type="color"
              value={displayValue}
              onChange={(e) => handleColorChange(e.target.value)}
              style={{
                opacity: 0,
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
